import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Brain, Heart, Activity, Sparkles, Target, TrendingUp } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useTrackerData } from '@/hooks/useTrackerData';
import { format } from 'date-fns';

interface MentalHealthData {
  date: string;
  mood_rating: number;
  emotions: string[];
  thought_patterns: string[];
  coping_behaviors: string[];
  energy_level: number;
  mind_clarity: number;
  spiritual_connection: number;
  reflection_notes: string;
}

const EMOTIONS = [
  { value: 'joyful', label: 'Joyful', color: 'bg-secondary' },
  { value: 'content', label: 'Content', color: 'bg-accent' },
  { value: 'calm', label: 'Calm', color: 'bg-primary' },
  { value: 'anxious', label: 'Anxious', color: 'bg-secondary' },
  { value: 'stressed', label: 'Stressed', color: 'bg-destructive' },
  { value: 'sad', label: 'Sad', color: 'bg-primary' },
  { value: 'angry', label: 'Angry', color: 'bg-destructive' },
  { value: 'overwhelmed', label: 'Overwhelmed', color: 'bg-accent' },
  { value: 'lonely', label: 'Lonely', color: 'bg-muted-foreground' },
  { value: 'hopeful', label: 'Hopeful', color: 'bg-accent' },
];

const THOUGHT_PATTERNS = [
  { value: 'clear', label: '✨ Clear & Focused' },
  { value: 'positive', label: '🌟 Positive & Optimistic' },
  { value: 'neutral', label: '😐 Neutral & Balanced' },
  { value: 'ruminating', label: '🔄 Ruminating' },
  { value: 'catastrophizing', label: '⚠️ Catastrophizing' },
  { value: 'critical', label: '❌ Self-Critical' },
  { value: 'racing', label: '⚡ Racing Thoughts' },
];

const COPING_BEHAVIORS = [
  { value: 'exercise', label: '💪 Exercise' },
  { value: 'meditation', label: '🧘 Meditation' },
  { value: 'journaling', label: '📝 Journaling' },
  { value: 'social_connection', label: '👥 Social Connection' },
  { value: 'creative', label: '🎨 Creative Activity' },
  { value: 'nature', label: '🌳 Time in Nature' },
  { value: 'avoidance', label: '🚪 Avoidance' },
  { value: 'substance_use', label: '🍷 Substance Use' },
  { value: 'self_harm', label: '⚠️ Self-Harm' },
  { value: 'isolation', label: '🔒 Isolation' },
];

export default function MentalHealthCheckIn() {
  const { toast } = useToast();
  const { saveTrackerData, loadTrackerData } = useTrackerData();
  const [isLoading, setIsLoading] = useState(false);
  
  const [formData, setFormData] = useState<MentalHealthData>({
    date: format(new Date(), 'yyyy-MM-dd'),
    mood_rating: 5,
    emotions: [],
    thought_patterns: [],
    coping_behaviors: [],
    energy_level: 5,
    mind_clarity: 5,
    spiritual_connection: 5,
    reflection_notes: '',
  });

  const toggleSelection = (field: 'emotions' | 'thought_patterns' | 'coping_behaviors', value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    const saved = await saveTrackerData('mental_wellness_daily', formData, true);
    setIsLoading(false);
    
    if (saved) {
      toast({
        title: "Mental Health Check-In Saved",
        description: "Your reflection has been recorded. This data helps Therappi® provide personalised support.",
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-gradient-to-br from-accent/10 via-primary/10 to-muted/10">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-full bg-gradient-to-br from-accent/20 to-primary/20">
            <Brain className="w-6 h-6 text-accent" />
          </div>
          <div>
            <h3 className="text-2xl font-bold">Evening Mental Health Check-In</h3>
            <p className="text-sm text-muted-foreground">
              Reflect on your inner experience today • 3-5 minutes
            </p>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          This data powers your Mental Wellness Score and helps Therappi® understand your patterns.
        </p>
      </Card>

      {/* Mood Rating */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Heart className="w-5 h-5 text-secondary" />
          <Label className="text-lg font-semibold">Overall Mood Today</Label>
        </div>
        <div className="space-y-4">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Very Low</span>
            <span className="text-2xl font-bold text-foreground">{formData.mood_rating}/10</span>
            <span>Excellent</span>
          </div>
          <Slider
            value={[formData.mood_rating]}
            onValueChange={([value]) => setFormData(prev => ({ ...prev, mood_rating: value }))}
            min={1}
            max={10}
            step={1}
            className="w-full"
          />
        </div>
      </Card>

      {/* Emotions */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Heart className="w-5 h-5 text-secondary" />
          <Label className="text-lg font-semibold">Emotions Experienced</Label>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Select all that apply</p>
        <div className="flex flex-wrap gap-2">
          {EMOTIONS.map(emotion => (
            <Badge
              key={emotion.value}
              variant={formData.emotions.includes(emotion.value) ? 'default' : 'outline'}
              className="cursor-pointer transition-all hover:scale-105"
              onClick={() => toggleSelection('emotions', emotion.value)}
            >
              {emotion.label}
            </Badge>
          ))}
        </div>
      </Card>

      {/* Thought Patterns */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Brain className="w-5 h-5 text-accent" />
          <Label className="text-lg font-semibold">Thought Patterns</Label>
        </div>
        <p className="text-sm text-muted-foreground mb-4">How would you describe your thoughts today?</p>
        <div className="flex flex-wrap gap-2">
          {THOUGHT_PATTERNS.map(pattern => (
            <Badge
              key={pattern.value}
              variant={formData.thought_patterns.includes(pattern.value) ? 'default' : 'outline'}
              className="cursor-pointer transition-all hover:scale-105"
              onClick={() => toggleSelection('thought_patterns', pattern.value)}
            >
              {pattern.label}
            </Badge>
          ))}
        </div>
      </Card>

      {/* Coping Behaviours */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-5 h-5 text-primary" />
          <Label className="text-lg font-semibold">Coping Strategies Used</Label>
        </div>
        <p className="text-sm text-muted-foreground mb-4">What did you do to manage emotions today?</p>
        <div className="flex flex-wrap gap-2">
          {COPING_BEHAVIORS.map(behaviour => (
            <Badge
              key={behaviour.value}
              variant={formData.coping_behaviors.includes(behaviour.value) ? 'default' : 'outline'}
              className="cursor-pointer transition-all hover:scale-105"
              onClick={() => toggleSelection('coping_behaviors', behaviour.value)}
            >
              {behaviour.label}
            </Badge>
          ))}
        </div>
      </Card>

      {/* Energy & Clarity */}
      <Card className="p-6">
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-secondary" />
              <Label className="font-semibold">Energy Level</Label>
            </div>
            <Slider
              value={[formData.energy_level]}
              onValueChange={([value]) => setFormData(prev => ({ ...prev, energy_level: value }))}
              min={1}
              max={10}
              step={1}
            />
            <div className="text-center mt-2 font-bold">{formData.energy_level}/10</div>
          </div>
          
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Brain className="w-5 h-5 text-primary" />
              <Label className="font-semibold">Mind Clarity</Label>
            </div>
            <Slider
              value={[formData.mind_clarity]}
              onValueChange={([value]) => setFormData(prev => ({ ...prev, mind_clarity: value }))}
              min={1}
              max={10}
              step={1}
            />
            <div className="text-center mt-2 font-bold">{formData.mind_clarity}/10</div>
          </div>
          
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Target className="w-5 h-5 text-accent" />
              <Label className="font-semibold">Spiritual Connection</Label>
            </div>
            <Slider
              value={[formData.spiritual_connection]}
              onValueChange={([value]) => setFormData(prev => ({ ...prev, spiritual_connection: value }))}
              min={1}
              max={10}
              step={1}
            />
            <div className="text-center mt-2 font-bold">{formData.spiritual_connection}/10</div>
          </div>
        </div>
      </Card>

      {/* Reflection Notes */}
      <Card className="p-6">
        <Label className="text-lg font-semibold mb-4 block">Reflection & Notes</Label>
        <Textarea
          placeholder="What insights, patterns, or important moments happened today? What are you grateful for? What challenged you?"
          value={formData.reflection_notes}
          onChange={(e) => setFormData(prev => ({ ...prev, reflection_notes: e.target.value }))}
          className="min-h-[120px]"
        />
      </Card>

      {/* Save Button */}
      <Button 
        onClick={handleSave} 
        disabled={isLoading}
        size="lg"
        className="w-full"
      >
        {isLoading ? 'Saving...' : 'Complete Check-In'}
      </Button>

      {/* Info Card */}
      <Card className="p-4 bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
        <div className="flex items-start gap-2">
          <TrendingUp className="w-5 h-5 text-primary mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold mb-1">Why This Matters</p>
            <p className="text-muted-foreground">
              Daily mental health tracking increases self-awareness by 68% and helps predict emotional patterns.
              This data enables Therappi® to provide personalised, context-aware support and recommend targeted protocols.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
